import { createClient } from '@/lib/supabase/client'

export interface AuditLogParams {
  action: string
  resourceType: string
  resourceId?: string
  userId?: string // Optional: if provided, skips getUser() call for better performance
  metadata?: Record<string, unknown>
}

/**
 * Logs an audit event. Optimized for performance:
 * - Accepts userId to avoid redundant getUser() call (recommended for better performance)
 * - Fails silently to not break app flow
 * 
 * @param params - Audit log parameters
 * @returns Promise that resolves when logging completes (can be fire-and-forget)
 * 
 * Performance tip: Pass userId when available to skip getUser() call.
 * For non-critical logs, don't await the result (fire-and-forget).
 */
export async function logAuditEvent(params: AuditLogParams): Promise<void> {
  let userId: string | undefined = params.userId
  
  try {
    const supabase = createClient()
    
    // Use provided userId if available, otherwise get from auth
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        // If no user, can't log
        console.warn('Audit logging skipped: no authenticated user')
        return
      }
      userId = user.id
    }
    
    // Get user agent from browser (if available)
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null
    
    // Insert audit log
    const { error, data } = await supabase.from('audit_logs').insert({
      user_id: userId,
      action: params.action,
      resource_type: params.resourceType,
      resource_id: params.resourceId || null,
      metadata: params.metadata || {},
      user_agent: userAgent,
    }).select()
    
    if (error) {
      // Log detailed error information for debugging
      console.error('[AUDIT LOG ERROR] Failed to log audit event:', {
        action: params.action,
        resourceType: params.resourceType,
        resourceId: params.resourceId,
        userId,
        error: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        errorHint: error.hint,
      })
    }
  } catch (error: any) {
    // Fail silently - audit logging should not break the app
    // But log detailed error for debugging
    console.error('[AUDIT LOG ERROR] Exception logging audit event:', {
      action: params.action,
      resourceType: params.resourceType,
      resourceId: params.resourceId,
      userId,
      errorMessage: error?.message,
      errorStack: error?.stack,
    })
  }
}

